import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  cn,
  formatNumber,
  formatPercent,
  timeAgo,
  formatDate,
  formatDuration,
  getStatusColor,
} from '@/lib/utils';
import { apiService as mockApi } from '@/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardHeader, CardTitle, CardBody, StatCard } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Modal, ConfirmDialog } from '@/components/ui/Modal';
import { Drawer } from '@/components/ui/Drawer';
import { Tabs } from '@/components/ui/Tabs';
import { LoadingState, EmptyState } from '@/components/ui/States';
import type {
  Device,
  Job,
  Script,
  ScriptParameter,
  ScriptVersion,
  Site,
  Client,
} from '@/types';
import {
  FileCode,
  Terminal,
  Plus,
  Search,
  Filter,
  CheckCircle2,
  XCircle,
  Clock,
  Play,
  Copy,
  Check,
  Tag,
  Shield,
  User,
  History,
  Code2,
  Sliders,
  ChevronRight,
  X,
  AlertCircle,
  FileText,
  RefreshCw,
  Cpu,
  Layers,
  ArrowRight,
  CheckSquare,
  Square,
  Send,
  Trash2,
} from 'lucide-react';

export function Scripts() {
  const navigate = useNavigate();

  // State
  const [scripts, setScripts] = useState<Script[]>([]);
  const [devices, setDevices] = useState<Device[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  // Filters
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [approvalFilter, setApprovalFilter] = useState<string>('all');

  // Script Detail Drawer state
  const [selectedScript, setSelectedScript] = useState<Script | null>(null);
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<string>('code');
  const [copiedCode, setCopiedCode] = useState<boolean>(false);

  // New Script Modal state
  const [newScriptModalOpen, setNewScriptModalOpen] = useState<boolean>(false);
  const [newScriptName, setNewScriptName] = useState<string>('');
  const [newScriptType, setNewScriptType] = useState<'powershell' | 'cmd'>('powershell');
  const [newScriptCategory, setNewScriptCategory] = useState<string>('Maintenance');
  const [newScriptTags, setNewScriptTags] = useState<string>('utility, system');
  const [newScriptDescription, setNewScriptDescription] = useState<string>('');
  const [newScriptContent, setNewScriptContent] = useState<string>(
    `# Write your automation script here\nWrite-Output "Executing InsightNode script on $env:COMPUTERNAME"`
  );
  const [newScriptTimeout, setNewScriptTimeout] = useState<number>(300);
  const [newScriptContext, setNewScriptContext] = useState<'system' | 'user'>('system');
  const [newScriptMaxRetries, setNewScriptMaxRetries] = useState<number>(1);
  const [newScriptRetryDelay, setNewScriptRetryDelay] = useState<number>(30);
  const [newScriptParams, setNewScriptParams] = useState<ScriptParameter[]>([]);

  // Parameter editor form temp states
  const [paramName, setParamName] = useState<string>('');
  const [paramType, setParamType] = useState<'string' | 'int' | 'bool' | 'choice'>('string');
  const [paramRequired, setParamRequired] = useState<boolean>(false);
  const [paramDefault, setParamDefault] = useState<string>('');
  const [paramDesc, setParamDesc] = useState<string>('');

  // Run Script Flow Wizard state
  const [runModalOpen, setRunModalOpen] = useState<boolean>(false);
  const [scriptToRun, setScriptToRun] = useState<Script | null>(null);
  const [runStep, setRunStep] = useState<1 | 2 | 3>(1);
  const [selectedDeviceIds, setSelectedDeviceIds] = useState<string[]>([]);
  const [deviceSearch, setDeviceSearch] = useState<string>('');
  const [runParamValues, setRunParamValues] = useState<Record<string, string>>({});
  const [runPriority, setRunPriority] = useState<'low' | 'normal' | 'high'>('normal');
  const [jobCreatedSuccess, setJobCreatedSuccess] = useState<boolean>(false);

  // Load scripts & devices
  const fetchScriptsData = async () => {
    setLoading(true);
    try {
      const [scriptsData, devicesResp] = await Promise.all([
        mockApi.getScripts(),
        mockApi.getDevices({ pageSize: 500 }),
      ]);
      setScripts([...scriptsData]);
      setDevices(devicesResp.data);
    } catch (err) {
      console.error('Failed to load scripts:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchScriptsData();
  }, []);

  // Unique categories list for filter dropdown
  const categories = useMemo(() => {
    const set = new Set<string>();
    scripts.forEach((s) => set.add(s.category));
    return Array.from(set);
  }, [scripts]);

  // Filtered scripts list
  const filteredScripts = useMemo(() => {
    return scripts.filter((s) => {
      // Type
      if (typeFilter !== 'all' && s.type !== typeFilter) return false;

      // Category
      if (categoryFilter !== 'all' && s.category !== categoryFilter) return false;

      // Approval Status
      if (approvalFilter !== 'all' && s.approvalStatus !== approvalFilter) return false;

      // Search Query
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const matchesName = s.name.toLowerCase().includes(q);
        const matchesDesc = s.description.toLowerCase().includes(q);
        const matchesCategory = s.category.toLowerCase().includes(q);
        const matchesTags = s.tags.some((t) => t.toLowerCase().includes(q));

        if (!matchesName && !matchesDesc && !matchesCategory && !matchesTags) return false;
      }

      return true;
    });
  }, [scripts, typeFilter, categoryFilter, approvalFilter, searchQuery]);

  // Summary Metrics
  const stats = useMemo(() => {
    const total = scripts.length;
    const approved = scripts.filter((s) => s.approvalStatus === 'approved').length;
    const pending = scripts.filter((s) => s.approvalStatus === 'pending').length;
    const powershell = scripts.filter((s) => s.type === 'powershell').length;
    const cmd = scripts.filter((s) => s.type === 'cmd').length;

    return { total, approved, pending, powershell, cmd };
  }, [scripts]);

  // Copy code handler
  const handleCopyCode = (code: string) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(true);
    setTimeout(() => setCopiedCode(false), 2000);
  };

  // Add parameter to new script
  const handleAddParameter = () => {
    if (!paramName.trim()) return;
    const newParam: ScriptParameter = {
      name: paramName.trim(),
      type: paramType,
      required: paramRequired,
      defaultValue: paramDefault,
      description: paramDesc,
    };
    setNewScriptParams([...newScriptParams, newParam]);
    setParamName('');
    setParamDefault('');
    setParamDesc('');
    setParamRequired(false);
  };

  const handleRemoveParameter = (index: number) => {
    setNewScriptParams(newScriptParams.filter((_, i) => i !== index));
  };

  // Handle Save New Script
  const handleSaveNewScript = async () => {
    if (!newScriptName.trim() || !newScriptContent.trim()) return;

    const tagsArray = newScriptTags
      .split(',')
      .map((t) => t.trim())
      .filter((t) => t.length > 0);

    const createdScript: Script = {
      id: `script-${Math.random().toString(36).substring(2, 9)}`,
      name: newScriptName.trim(),
      type: newScriptType,
      category: newScriptCategory || 'General',
      tags: tagsArray.length > 0 ? tagsArray : ['custom'],
      description: newScriptDescription,
      content: newScriptContent,
      parameters: newScriptParams,
      version: 1,
      approvalStatus: 'approved', // Auto-approve created scripts for convenience
      approvedBy: 'Administrator',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      versions: [
        {
          version: 1,
          content: newScriptContent,
          changedBy: 'Administrator',
          changedAt: new Date().toISOString(),
          changeNote: 'Initial draft creation',
        },
      ],
      timeoutSec: newScriptTimeout,
      executionContext: newScriptContext,
      retryPolicy: {
        maxRetries: newScriptMaxRetries,
        delaySec: newScriptRetryDelay,
      },
    };

    // Update in memory store and state
    await mockApi.createScript(createdScript);
    setScripts([createdScript, ...scripts]);

    // Reset & Close Modal
    setNewScriptModalOpen(false);
    setNewScriptName('');
    setNewScriptDescription('');
    setNewScriptContent(
      `# Write your automation script here\nWrite-Output "Executing InsightNode script on $env:COMPUTERNAME"`
    );
    setNewScriptParams([]);
  };

  // Approval Status toggle handler
  const handleToggleApproval = (script: Script, status: 'approved' | 'rejected' | 'pending') => {
    const updated = scripts.map((s) => {
      if (s.id === script.id) {
        return {
          ...s,
          approvalStatus: status,
          approvedBy: status === 'approved' ? 'Administrator' : null,
          updatedAt: new Date().toISOString(),
        };
      }
      return s;
    });

    setScripts(updated);
    if (selectedScript?.id === script.id) {
      setSelectedScript((prev) =>
        prev
          ? {
              ...prev,
              approvalStatus: status,
              approvedBy: status === 'approved' ? 'Administrator' : null,
              updatedAt: new Date().toISOString(),
            }
          : null
      );
    }
  };

  // Open Run Script Wizard
  const handleOpenRunWizard = (script: Script) => {
    setScriptToRun(script);
    setSelectedDeviceIds([]);
    setRunStep(1);

    // Populate default parameter values
    const initialParams: Record<string, string> = {};
    script.parameters?.forEach((p) => {
      initialParams[p.name] = p.defaultValue || '';
    });
    setRunParamValues(initialParams);
    setJobCreatedSuccess(false);
    setRunModalOpen(true);
  };

  // Confirm Run Script -> Create Mock Job
  const handleExecuteJob = async () => {
    if (!scriptToRun || selectedDeviceIds.length === 0) return;

    const targetDevicesList = devices.filter((d) => selectedDeviceIds.includes(d.id));

    const newJob: Job = {
      id: `job-${Math.random().toString(36).substring(2, 9)}`,
      tenantId: 'tenant-01',
      type: 'script',
      name: `Run: ${scriptToRun.name}`,
      status: 'running',
      priority: runPriority,
      targetDevices: targetDevicesList.map((d) => d.hostname),
      targetCount: targetDevicesList.length,
      completedCount: 0,
      failedCount: 0,
      initiatedBy: 'Administrator',
      createdAt: new Date().toISOString(),
      startedAt: new Date().toISOString(),
      completedAt: null,
      progress: 0,
      exitCode: null,
      results: targetDevicesList.map((d) => ({
        deviceId: d.id,
        hostname: d.hostname,
        status: 'running',
        exitCode: null,
        output: `[${new Date().toLocaleTimeString()}] Executing script '${scriptToRun.name}' (v${scriptToRun.version})...\nContext: ${scriptToRun.executionContext.toUpperCase()}\nTimeout: ${scriptToRun.timeoutSec}s`,
        duration: 2,
        startedAt: new Date().toISOString(),
        completedAt: null,
        error: null,
      })),
    };

    // Store in global store
    await mockApi.createJob(newJob);
    setJobCreatedSuccess(true);
  };

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <PageHeader title="Automation Library" description="Script library & remote deployment workflows" />
        <LoadingState rows={8} />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 bg-[var(--bg)] min-h-[calc(100vh-4rem)]">
      {/* Top Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-[var(--text-primary)]">Scripts Library</h1>
          <p className="text-xs text-[var(--text-muted)] mt-1">
            Author, review, approve, and execute PowerShell & CMD automation scripts.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Button size="sm" variant="primary" onClick={() => setNewScriptModalOpen(true)}>
            <Plus className="h-4 w-4" />
            New Script
          </Button>
        </div>
      </div>

      {/* KPI Stats Row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Total Scripts" value={stats.total} sublabel="In workspace library" />
        <StatCard label="Approved & Ready" value={stats.approved} status="online" sublabel="Eligible for execution" />
        <StatCard label="Pending Approval" value={stats.pending} status="warning" sublabel="Requires security review" />
        <StatCard label="Script Runtime Types" value={`${stats.powershell} PS / ${stats.cmd} CMD`} sublabel="Cross-platform supported" />
      </div>

      {/* Search & Filter Toolbar */}
      <Card>
        <CardBody className="p-4 space-y-3">
          <div className="flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-3">
            {/* Search Input */}
            <div className="relative flex-1 min-w-[240px]">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-[var(--text-muted)]" />
              <input
                type="text"
                placeholder="Search by script name, tags, description..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full bg-[var(--elevated)] border border-[var(--border)] rounded-md pl-9 pr-8 py-1.5 text-xs text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:outline-none focus:border-[var(--accent)]"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2.5 top-2.5 text-[var(--text-muted)] hover:text-[var(--text-primary)]"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>

            {/* Filter Dropdowns */}
            <div className="flex flex-wrap items-center gap-2">
              {/* Type Filter */}
              <div className="flex items-center gap-1.5 bg-[var(--elevated)] border border-[var(--border)] px-2.5 py-1 rounded-md text-xs">
                <span className="text-[var(--text-muted)] font-medium">Type:</span>
                <select
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  className="bg-transparent text-[var(--text-primary)] focus:outline-none capitalize font-medium"
                >
                  <option value="all">All Runtimes</option>
                  <option value="powershell">PowerShell</option>
                  <option value="cmd">Command Prompt (CMD)</option>
                </select>
              </div>

              {/* Category Filter */}
              <div className="flex items-center gap-1.5 bg-[var(--elevated)] border border-[var(--border)] px-2.5 py-1 rounded-md text-xs">
                <span className="text-[var(--text-muted)] font-medium">Category:</span>
                <select
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  className="bg-transparent text-[var(--text-primary)] focus:outline-none capitalize font-medium"
                >
                  <option value="all">All Categories</option>
                  {categories.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>

              {/* Approval Filter */}
              <div className="flex items-center gap-1.5 bg-[var(--elevated)] border border-[var(--border)] px-2.5 py-1 rounded-md text-xs">
                <span className="text-[var(--text-muted)] font-medium">Status:</span>
                <select
                  value={approvalFilter}
                  onChange={(e) => setApprovalFilter(e.target.value)}
                  className="bg-transparent text-[var(--text-primary)] focus:outline-none capitalize font-medium"
                >
                  <option value="all">All Statuses</option>
                  <option value="approved">Approved</option>
                  <option value="pending">Pending Approval</option>
                  <option value="draft">Draft</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>

              {(typeFilter !== 'all' || categoryFilter !== 'all' || approvalFilter !== 'all' || searchQuery) && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setTypeFilter('all');
                    setCategoryFilter('all');
                    setApprovalFilter('all');
                    setSearchQuery('');
                  }}
                  className="text-xs text-[var(--text-muted)]"
                >
                  Reset
                </Button>
              )}
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Scripts Table */}
      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs border-collapse">
            <thead>
              <tr className="border-b border-[var(--border)] bg-[var(--elevated)] text-[var(--text-muted)] font-semibold uppercase text-2xs tracking-wider">
                <th className="px-4 py-3">Script Name & Description</th>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Category</th>
                <th className="px-4 py-3">Tags</th>
                <th className="px-4 py-3">Version</th>
                <th className="px-4 py-3">Approval</th>
                <th className="px-4 py-3">Context & Timeout</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--border)] text-[var(--text-primary)]">
              {filteredScripts.length === 0 ? (
                <tr>
                  <td colSpan={8} className="py-12">
                    <EmptyState
                      icon={<FileCode className="h-8 w-8 text-[var(--text-muted)]" />}
                      title="No scripts found"
                      description="No automation scripts match the active filters."
                    />
                  </td>
                </tr>
              ) : (
                filteredScripts.map((script) => (
                  <tr
                    key={script.id}
                    onClick={() => {
                      setSelectedScript(script);
                      setDrawerOpen(true);
                      setActiveTab('code');
                    }}
                    className="hover:bg-[var(--hover)] transition-colors cursor-pointer group"
                  >
                    {/* Name & Description */}
                    <td className="px-4 py-3 max-w-xs">
                      <div className="font-semibold text-[var(--text-primary)] group-hover:text-[var(--accent)] transition-colors truncate">
                        {script.name}
                      </div>
                      <div className="text-2xs text-[var(--text-muted)] truncate mt-0.5">
                        {script.description || 'No description provided.'}
                      </div>
                    </td>

                    {/* Type Badge */}
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span
                        className={cn(
                          'text-2xs font-mono font-medium px-2 py-0.5 rounded border uppercase',
                          script.type === 'powershell'
                            ? 'text-blue-400 bg-blue-500/10 border-blue-500/20'
                            : 'text-zinc-400 bg-zinc-500/10 border-zinc-500/20'
                        )}
                      >
                        {script.type}
                      </span>
                    </td>

                    {/* Category */}
                    <td className="px-4 py-3 whitespace-nowrap text-[var(--text-secondary)] font-medium">
                      {script.category}
                    </td>

                    {/* Tags */}
                    <td className="px-4 py-3 max-w-[160px]">
                      <div className="flex flex-wrap gap-1">
                        {script.tags.map((t) => (
                          <span
                            key={t}
                            className="text-2xs bg-[var(--elevated)] border border-[var(--border)] text-[var(--text-muted)] px-1.5 py-0.5 rounded"
                          >
                            #{t}
                          </span>
                        ))}
                      </div>
                    </td>

                    {/* Version */}
                    <td className="px-4 py-3 whitespace-nowrap text-2xs font-mono text-[var(--text-muted)]">
                      v{script.version}.0
                    </td>

                    {/* Approval Status Badge */}
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span
                        className={cn(
                          'text-2xs font-medium px-2 py-0.5 rounded capitalize border inline-flex items-center gap-1',
                          script.approvalStatus === 'approved' &&
                            'text-emerald-400 bg-emerald-500/10 border-emerald-500/20',
                          script.approvalStatus === 'pending' &&
                            'text-amber-400 bg-amber-500/10 border-amber-500/20',
                          script.approvalStatus === 'draft' &&
                            'text-zinc-400 bg-zinc-500/10 border-zinc-500/20',
                          script.approvalStatus === 'rejected' &&
                            'text-rose-400 bg-rose-500/10 border-rose-500/20'
                        )}
                      >
                        {script.approvalStatus === 'approved' && <CheckCircle2 className="h-3 w-3" />}
                        {script.approvalStatus === 'pending' && <Clock className="h-3 w-3" />}
                        {script.approvalStatus === 'rejected' && <XCircle className="h-3 w-3" />}
                        {script.approvalStatus}
                      </span>
                    </td>

                    {/* Context & Timeout */}
                    <td className="px-4 py-3 whitespace-nowrap text-2xs text-[var(--text-muted)]">
                      <div className="uppercase font-mono text-[var(--text-secondary)]">
                        {script.executionContext}
                      </div>
                      <div>Timeout: {script.timeoutSec}s</div>
                    </td>

                    {/* Actions */}
                    <td className="px-4 py-3 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-1">
                        {script.approvalStatus === 'approved' ? (
                          <Button
                            size="sm"
                            variant="primary"
                            onClick={() => handleOpenRunWizard(script)}
                          >
                            <Play className="h-3.5 w-3.5" />
                            Run Script
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => handleToggleApproval(script, 'approved')}
                          >
                            Approve
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Script Details Drawer */}
      {selectedScript && (
        <Drawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          title={`Script Inspector: ${selectedScript.name}`}
          width={560}
        >
          <div className="p-5 space-y-5">
            {/* Header Metadata */}
            <div className="bg-[var(--elevated)] border border-[var(--border)] rounded-md p-4 space-y-3">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-sm font-semibold text-[var(--text-primary)]">{selectedScript.name}</h3>
                  <p className="text-xs text-[var(--text-muted)] mt-0.5">{selectedScript.description}</p>
                </div>

                {selectedScript.approvalStatus === 'approved' ? (
                  <Button size="sm" variant="primary" onClick={() => handleOpenRunWizard(selectedScript)}>
                    <Play className="h-3.5 w-3.5" />
                    Run
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => handleToggleApproval(selectedScript, 'approved')}
                  >
                    Submit Approval
                  </Button>
                )}
              </div>

              <div className="grid grid-cols-3 gap-2 text-2xs text-[var(--text-muted)] pt-2 border-t border-[var(--border)]">
                <div>
                  <span className="block text-[var(--text-muted)]">Type</span>
                  <span className="font-mono font-medium text-[var(--text-primary)] uppercase">
                    {selectedScript.type}
                  </span>
                </div>
                <div>
                  <span className="block text-[var(--text-muted)]">Category</span>
                  <span className="font-medium text-[var(--text-primary)]">{selectedScript.category}</span>
                </div>
                <div>
                  <span className="block text-[var(--text-muted)]">Status</span>
                  <span className="font-medium text-[var(--text-primary)] capitalize">
                    {selectedScript.approvalStatus}
                  </span>
                </div>
              </div>
            </div>

            {/* Navigation Tabs */}
            <Tabs
              tabs={[
                { id: 'code', label: 'Script Code' },
                { id: 'overview', label: 'Overview & Policy' },
                { id: 'parameters', label: `Parameters (${selectedScript.parameters?.length || 0})` },
                { id: 'history', label: 'Version History' },
              ]}
              defaultActive="code"
              onChange={(id) => setActiveTab(id)}
            />

            {/* Tab 1: Code Preview */}
            {activeTab === 'code' && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-2xs text-[var(--text-muted)] font-mono">
                    Runtime: {selectedScript.type.toUpperCase()} • {selectedScript.content.split('\n').length} lines
                  </span>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleCopyCode(selectedScript.content)}
                    className="h-7 px-2 text-2xs"
                  >
                    {copiedCode ? <Check className="h-3 w-3 text-emerald-400" /> : <Copy className="h-3 w-3" />}
                    {copiedCode ? 'Copied' : 'Copy Code'}
                  </Button>
                </div>

                <div className="bg-[#050507] border border-[var(--border)] rounded-md p-3 overflow-x-auto max-h-96">
                  <pre className="font-mono text-xs text-blue-300 leading-relaxed whitespace-pre-wrap">
                    {selectedScript.content}
                  </pre>
                </div>
              </div>
            )}

            {/* Tab 2: Overview & Policy */}
            {activeTab === 'overview' && (
              <div className="space-y-4 text-xs">
                <div className="p-3 bg-[var(--elevated)] border border-[var(--border)] rounded-md space-y-2">
                  <h4 className="font-semibold text-[var(--text-primary)]">Execution Environment</h4>
                  <div className="grid grid-cols-2 gap-3 text-2xs">
                    <div>
                      <span className="text-[var(--text-muted)] block">Execution Context</span>
                      <span className="font-mono font-medium text-[var(--text-primary)] uppercase">
                        {selectedScript.executionContext}
                      </span>
                    </div>
                    <div>
                      <span className="text-[var(--text-muted)] block">Timeout Limit</span>
                      <span className="font-medium text-[var(--text-primary)]">
                        {selectedScript.timeoutSec} seconds
                      </span>
                    </div>
                  </div>
                </div>

                <div className="p-3 bg-[var(--elevated)] border border-[var(--border)] rounded-md space-y-2">
                  <h4 className="font-semibold text-[var(--text-primary)]">Retry Policy</h4>
                  <div className="grid grid-cols-2 gap-3 text-2xs">
                    <div>
                      <span className="text-[var(--text-muted)] block">Max Automatic Retries</span>
                      <span className="font-medium text-[var(--text-primary)]">
                        {selectedScript.retryPolicy?.maxRetries || 0} times
                      </span>
                    </div>
                    <div>
                      <span className="text-[var(--text-muted)] block">Delay Between Retries</span>
                      <span className="font-medium text-[var(--text-primary)]">
                        {selectedScript.retryPolicy?.delaySec || 0} seconds
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Tab 3: Parameters List */}
            {activeTab === 'parameters' && (
              <div className="space-y-3 text-xs">
                {!selectedScript.parameters || selectedScript.parameters.length === 0 ? (
                  <p className="text-[var(--text-muted)] italic text-center py-6">
                    No input parameters required for this script.
                  </p>
                ) : (
                  selectedScript.parameters.map((p) => (
                    <div
                      key={p.name}
                      className="p-3 bg-[var(--elevated)] border border-[var(--border)] rounded-md space-y-1"
                    >
                      <div className="flex items-center justify-between font-mono">
                        <span className="font-semibold text-[var(--accent)]">${p.name}</span>
                        <span className="text-2xs bg-[var(--panel)] border border-[var(--border)] px-1.5 py-0.5 rounded text-[var(--text-muted)]">
                          {p.type} {p.required && '(Required)'}
                        </span>
                      </div>
                      <p className="text-2xs text-[var(--text-muted)]">{p.description || 'No description'}</p>
                      {p.defaultValue && (
                        <div className="text-2xs font-mono text-[var(--text-secondary)]">
                          Default: "{p.defaultValue}"
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Tab 4: Version History */}
            {activeTab === 'history' && (
              <div className="space-y-3 text-xs">
                {selectedScript.versions?.map((v) => (
                  <div key={v.version} className="p-3 bg-[var(--elevated)] border border-[var(--border)] rounded-md space-y-1">
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-[var(--text-primary)]">Version {v.version}.0</span>
                      <span className="text-2xs text-[var(--text-muted)]">{formatDate(v.changedAt)}</span>
                    </div>
                    <p className="text-2xs text-[var(--text-secondary)]">Author: {v.changedBy}</p>
                    <p className="text-2xs text-[var(--text-muted)]">{v.changeNote}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Drawer>
      )}

      {/* Author New Script Modal */}
      <Modal
        open={newScriptModalOpen}
        onClose={() => setNewScriptModalOpen(false)}
        title="Author New Script"
        size="xl"
      >
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4 text-xs">
            <div>
              <label className="block font-medium text-[var(--text-primary)] mb-1">Script Name</label>
              <input
                type="text"
                placeholder="e.g., Clear Temp Logs & Cache"
                value={newScriptName}
                onChange={(e) => setNewScriptName(e.target.value)}
                className="w-full bg-[var(--elevated)] border border-[var(--border)] rounded px-3 py-1.5 text-xs text-[var(--text-primary)] focus:outline-none focus:border-[var(--accent)]"
              />
            </div>

            <div>
              <label className="block font-medium text-[var(--text-primary)] mb-1">Runtime Type</label>
              <select
                value={newScriptType}
                onChange={(e) => setNewScriptType(e.target.value as any)}
                className="w-full bg-[var(--elevated)] border border-[var(--border)] rounded px-3 py-1.5 text-xs text-[var(--text-primary)] focus:outline-none focus:border-[var(--accent)]"
              >
                <option value="powershell">PowerShell (.ps1)</option>
                <option value="cmd">Command Prompt (.bat / .cmd)</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 text-xs">
            <div>
              <label className="block font-medium text-[var(--text-primary)] mb-1">Category</label>
              <input
                type="text"
                placeholder="e.g., Maintenance, Security"
                value={newScriptCategory}
                onChange={(e) => setNewScriptCategory(e.target.value)}
                className="w-full bg-[var(--elevated)] border border-[var(--border)] rounded px-3 py-1.5 text-xs text-[var(--text-primary)] focus:outline-none focus:border-[var(--accent)]"
              />
            </div>

            <div>
              <label className="block font-medium text-[var(--text-primary)] mb-1">Tags (Comma Separated)</label>
              <input
                type="text"
                placeholder="e.g., cleanup, logs, disk"
                value={newScriptTags}
                onChange={(e) => setNewScriptTags(e.target.value)}
                className="w-full bg-[var(--elevated)] border border-[var(--border)] rounded px-3 py-1.5 text-xs text-[var(--text-primary)] focus:outline-none focus:border-[var(--accent)]"
              />
            </div>
          </div>

          <div className="text-xs">
            <label className="block font-medium text-[var(--text-primary)] mb-1">Description</label>
            <textarea
              rows={2}
              placeholder="Describe what this automation script does..."
              value={newScriptDescription}
              onChange={(e) => setNewScriptDescription(e.target.value)}
              className="w-full bg-[var(--elevated)] border border-[var(--border)] rounded px-3 py-1.5 text-xs text-[var(--text-primary)] focus:outline-none focus:border-[var(--accent)]"
            />
          </div>

          {/* Code Editor TextArea */}
          <div className="text-xs">
            <label className="block font-medium text-[var(--text-primary)] mb-1">Script Source Code</label>
            <textarea
              rows={8}
              value={newScriptContent}
              onChange={(e) => setNewScriptContent(e.target.value)}
              className="w-full bg-[#050507] border border-[var(--border)] rounded px-3 py-2 font-mono text-xs text-blue-300 focus:outline-none focus:border-[var(--accent)]"
            />
          </div>

          {/* Action Footer */}
          <div className="flex justify-end gap-2 pt-2 border-t border-[var(--border)]">
            <Button size="sm" variant="ghost" onClick={() => setNewScriptModalOpen(false)}>
              Cancel
            </Button>
            <Button size="sm" variant="primary" onClick={handleSaveNewScript}>
              Save & Register Script
            </Button>
          </div>
        </div>
      </Modal>

      {/* Multi-Step Run Script Wizard Modal */}
      {scriptToRun && (
        <Modal
          open={runModalOpen}
          onClose={() => setRunModalOpen(false)}
          title={`Run Script Flow: ${scriptToRun.name}`}
          size="lg"
        >
          <div className="space-y-5">
            {/* Step Wizard Header */}
            <div className="grid grid-cols-3 gap-2 text-center text-xs border-b border-[var(--border)] pb-3">
              <div
                className={cn(
                  'py-1.5 rounded font-medium',
                  runStep === 1
                    ? 'bg-[var(--accent)] text-white'
                    : 'bg-[var(--elevated)] text-[var(--text-muted)]'
                )}
              >
                1. Select Target Devices ({selectedDeviceIds.length})
              </div>
              <div
                className={cn(
                  'py-1.5 rounded font-medium',
                  runStep === 2
                    ? 'bg-[var(--accent)] text-white'
                    : 'bg-[var(--elevated)] text-[var(--text-muted)]'
                )}
              >
                2. Parameters
              </div>
              <div
                className={cn(
                  'py-1.5 rounded font-medium',
                  runStep === 3
                    ? 'bg-[var(--accent)] text-white'
                    : 'bg-[var(--elevated)] text-[var(--text-muted)]'
                )}
              >
                3. Confirm Execution
              </div>
            </div>

            {/* Step 1: Device Picker */}
            {runStep === 1 && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <input
                    type="text"
                    placeholder="Filter devices..."
                    value={deviceSearch}
                    onChange={(e) => setDeviceSearch(e.target.value)}
                    className="bg-[var(--elevated)] border border-[var(--border)] rounded px-3 py-1 text-xs text-[var(--text-primary)]"
                  />
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      if (selectedDeviceIds.length === devices.length) setSelectedDeviceIds([]);
                      else setSelectedDeviceIds(devices.map((d) => d.id));
                    }}
                  >
                    {selectedDeviceIds.length === devices.length ? 'Deselect All' : 'Select All'}
                  </Button>
                </div>

                <div className="max-h-60 overflow-y-auto border border-[var(--border)] rounded divide-y divide-[var(--border)]">
                  {devices
                    .filter((d) => d.hostname.toLowerCase().includes(deviceSearch.toLowerCase()))
                    .map((dev) => {
                      const isChecked = selectedDeviceIds.includes(dev.id);
                      return (
                        <div
                          key={dev.id}
                          onClick={() => {
                            if (isChecked) setSelectedDeviceIds(selectedDeviceIds.filter((id) => id !== dev.id));
                            else setSelectedDeviceIds([...selectedDeviceIds, dev.id]);
                          }}
                          className="p-2.5 flex items-center justify-between text-xs cursor-pointer hover:bg-[var(--hover)]"
                        >
                          <div className="flex items-center gap-2">
                            {isChecked ? (
                              <CheckSquare className="h-4 w-4 text-[var(--accent)]" />
                            ) : (
                              <Square className="h-4 w-4 text-[var(--text-muted)]" />
                            )}
                            <span className="font-medium text-[var(--text-primary)]">{dev.hostname}</span>
                            <span className="text-2xs text-[var(--text-muted)]">({dev.site})</span>
                          </div>
                          <Badge variant={dev.status}>{dev.status}</Badge>
                        </div>
                      );
                    })}
                </div>

                <div className="flex justify-end gap-2 pt-2">
                  <Button
                    size="sm"
                    variant="primary"
                    disabled={selectedDeviceIds.length === 0}
                    onClick={() => setRunStep(2)}
                  >
                    Next: Parameters
                  </Button>
                </div>
              </div>
            )}

            {/* Step 2: Parameter Inputs */}
            {runStep === 2 && (
              <div className="space-y-4 text-xs">
                {!scriptToRun.parameters || scriptToRun.parameters.length === 0 ? (
                  <p className="text-[var(--text-muted)] italic text-center py-4">
                    This script does not require any parameters.
                  </p>
                ) : (
                  scriptToRun.parameters.map((p) => (
                    <div key={p.name}>
                      <label className="block font-medium text-[var(--text-primary)] mb-1">
                        ${p.name} {p.required && <span className="text-rose-400">*</span>}
                      </label>
                      <input
                        type="text"
                        value={runParamValues[p.name] || ''}
                        onChange={(e) =>
                          setRunParamValues({ ...runParamValues, [p.name]: e.target.value })
                        }
                        className="w-full bg-[var(--elevated)] border border-[var(--border)] rounded px-3 py-1.5 text-xs text-[var(--text-primary)]"
                      />
                      <p className="text-2xs text-[var(--text-muted)] mt-0.5">{p.description}</p>
                    </div>
                  ))
                )}

                <div className="flex justify-between gap-2 pt-2">
                  <Button size="sm" variant="ghost" onClick={() => setRunStep(1)}>
                    Back
                  </Button>
                  <Button size="sm" variant="primary" onClick={() => setRunStep(3)}>
                    Next: Confirmation
                  </Button>
                </div>
              </div>
            )}

            {/* Step 3: Execution Confirmation */}
            {runStep === 3 && (
              <div className="space-y-4 text-xs">
                {jobCreatedSuccess ? (
                  <div className="p-6 text-center space-y-3">
                    <CheckCircle2 className="h-10 w-10 text-emerald-400 mx-auto" />
                    <h3 className="text-sm font-semibold text-[var(--text-primary)]">
                      Job Dispatched Successfully!
                    </h3>
                    <p className="text-xs text-[var(--text-muted)]">
                      The execution job has been sent to {selectedDeviceIds.length} target devices.
                    </p>
                    <div className="pt-2 flex justify-center gap-2">
                      <Button size="sm" variant="secondary" onClick={() => setRunModalOpen(false)}>
                        Close Window
                      </Button>
                      <Button size="sm" variant="primary" onClick={() => navigate('/jobs')}>
                        Go to Jobs Page
                      </Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="p-3 bg-[var(--elevated)] border border-[var(--border)] rounded space-y-2">
                      <div>
                        <span className="text-2xs text-[var(--text-muted)] block">Script Name</span>
                        <span className="font-semibold text-[var(--text-primary)]">{scriptToRun.name}</span>
                      </div>
                      <div>
                        <span className="text-2xs text-[var(--text-muted)] block">Target Device Count</span>
                        <span className="font-semibold text-[var(--accent)]">
                          {selectedDeviceIds.length} Devices
                        </span>
                      </div>
                    </div>

                    <div className="flex justify-between gap-2 pt-2">
                      <Button size="sm" variant="ghost" onClick={() => setRunStep(2)}>
                        Back
                      </Button>
                      <Button size="sm" variant="primary" onClick={handleExecuteJob}>
                        Confirm & Dispatch Job
                      </Button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
}
